<template>
    <div class="bg-gray-100">
        <TopLocationHero title-key="promenade.title" static-image-key="promenade___hero" />

        <div class="relative mt-4 sm:mt-12 z-1">
            <a id="main-content" class="fixed top-0" tabindex="-1"></a>

            <div class="container hidden sm:block">
                <Breadcrumb :items="breadcrumbItems" />
            </div>

            <TopLocationBackgroundShape1 />
        </div>

        <div class="container relative z-2 mt-12">
            <!-- * -->
            <!-- * -->
            <!-- * -->
            <!-- INTRO -->
            <!-- * -->
            <Type3Block
                featured
                title-key="promenade.intro.title"
                description-key="promenade.intro.description"
                static-image-key="promenade___intro"
            />

            <!-- * -->
            <!-- * -->
            <!-- * -->
            <!-- Zenélő Szökőkút -->
            <!-- * -->
            <Type8Block
                title-key="promenade.fountain.title"
                description-key="promenade.fountain.description"
                class="mt-16 sm:mt-20"
            />

            <div class="grid grid-cols-12 base-responsive-gap mt-12">
                <div class="col-span-12 lg:col-span-8 lg:col-start-3">
                    <iframe
                        style="border-radius: 12px"
                        src="https://open.spotify.com/embed/playlist/3ArSqUCMA7YAiPON7xlI0I?utm_source=generator"
                        width="100%"
                        height="352"
                        frameBorder="0"
                        allowfullscreen=""
                        allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                        loading="lazy"
                        :aria-label="$t('promenade.fountain.spotify_playlist_1_name')"
                    ></iframe>
                </div>
            </div>

            <!-- * -->
            <!-- * -->
            <!-- * -->
            <!-- MOSAIQUE GALLERY -->
            <!-- * -->
            <MosaiqueGalleryBlock v-if="galleryImages1" :items="galleryImages1" class="mt-16 sm:mt-20" />

            <div class="grid grid-cols-12 base-responsive-gap mt-16 sm:mt-20 z-2">
                <div class="col-span-12 lg:col-span-8 lg:col-start-3">
                    <p><T t="promenade.music.description_1" /></p>

                    <div class="my-12">
                        <iframe
                            style="border-radius: 12px"
                            src="https://open.spotify.com/embed/playlist/3h1rmBegF5AFQg3peF8XKX?utm_source=generator"
                            width="100%"
                            height="352"
                            frameBorder="0"
                            allowfullscreen=""
                            allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                            loading="lazy"
                            :aria-label="$t('promenade.fountain.spotify_playlist_2_name')"
                        ></iframe>
                    </div>

                    <p><T t="promenade.music.description_2" /></p>
                </div>
            </div>
        </div>

        <!-- * -->
        <!-- * -->
        <!-- * -->
        <!-- GREEN PROMENADE -->
        <!-- * -->
        <div v-if="galleryImages2" class="relative z-1 mt-16 sm:mt-20">
            <Type2Block
                title-key="promenade.green_promenade.title"
                description-key="promenade.green_promenade.description"
                :images="galleryImages2"
                class="relative z-1"
            />

            <TopLocationBackgroundShape4 flip-y class="-bottom-12" />
        </div>

        <!-- * -->
        <!-- * -->
        <!-- * -->
        <!-- ACCORDION -->
        <!-- * -->
        <div v-if="galleryImages3" class="container relative z-2 mt-16 sm:mt-20">
            <Type5Block :items="galleryImages3" />
        </div>

        <div class="container mt-16 sm:mt-20">
            <TopLocationProjectPageBanner
                v-if="$t('promenade.event_recommendation_button_url').length && false"
                :button-text="$t('promenade.event_recommendation_button_label')"
                :button-url="$t('promenade.event_recommendation_button_url')"
                label-text-key="promenade.event_recommendation_text"
                type="event"
            />

            <TopLocationProjectPageBanner
                :button-text="$t('promenade.visit_project_button_label')"
                :button-url="$t('promenade.visit_project_button_url')"
                label-text-key="promenade.visit_project_text"
                class="mt-4 sm:mt-8"
            />

            <AwardsBlock
                v-if="awards"
                :title="awards.title"
                :description="awards.description"
                :media="awards.media"
                class="mt-16 sm:mt-20"
            />
        </div>
    </div>
</template>

<script>
import TopLocationProjectPageBanner from '@/components/Pages/TopLocation/TopLocationProjectPageBanner.vue';
import AwardsBlock from '@/components/Blocks/AwardsBlock/AwardsBlock.vue';
import TopLocationHero from '@/components/Pages/TopLocation/Hero/Hero.vue';
import Type3Block from '@/components/Blocks/Type3Block/Type3Block.vue';
import Type2Block from '@/components/Blocks/Type2Block/Type2Block.vue';
import Type5Block from '@/components/Blocks/Type5Block/Type5Block.vue';
import TopLocationBackgroundShape1 from '@/components/Pages/TopLocation/TopLocationBackgroundShape1.vue';
import Type8Block from '@/components/Blocks/Type8Block/Type8Block.vue';
import MosaiqueGalleryBlock from '@/components/Blocks/MosaiqueGalleryBlock/MosaiqueGalleryBlock.vue';
import TopLocationBackgroundShape4 from '@/components/Pages/TopLocation/TopLocationBackgroundShape4.vue';

export default {
    name: 'VarosligetPromenade',
    components: {
        AwardsBlock,
        TopLocationProjectPageBanner,
        TopLocationBackgroundShape4,
        MosaiqueGalleryBlock,
        Type8Block,
        TopLocationBackgroundShape1,
        TopLocationHero,
        Type3Block,
        Type2Block,
        Type5Block,
    },
    async asyncData({ $axios, i18n }) {
        let galleryImages1 = null;
        let galleryImages2 = null;
        let galleryImages3 = null;
        let awards = null;

        await Promise.allSettled([
            $axios.$get('/galleries/reference/top-location___promenade-1'),
            $axios.$get('/galleries/reference/top-location___promenade-2'),
            $axios.$get('/galleries/reference/top-location___promenade-3'),
            $axios.$get('/awards/reference/promenade'),
        ])
            .then(([galleryImages1Res, galleryImages2Res, galleryImages3Res, awardsRes]) => {
                if (galleryImages1Res.status === 'fulfilled') {
                    galleryImages1 = galleryImages1Res.value.slides;
                }

                if (galleryImages2Res.status === 'fulfilled') {
                    galleryImages2 = galleryImages2Res.value.slides;
                }

                if (galleryImages3Res.status === 'fulfilled') {
                    galleryImages3 = galleryImages3Res.value.slides;
                }

                if (awardsRes.status === 'fulfilled' && awardsRes.value.is_on_top_locations) {
                    awards = awardsRes.value;
                }
            })
            .catch((err) => {
                console.log(err);
            });

        const breadcrumbItems = [];

        breadcrumbItems.push([i18n.t('navigation.top_locations'), 'top-locations', true]);
        breadcrumbItems.push([i18n.t('promenade.title'), 'varosliget-promenade']);

        return {
            galleryImages1,
            galleryImages2,
            galleryImages3,
            awards,
            breadcrumbItems,
        };
    },
    data() {
        return {};
    },
    head() {
        return {
            title: this.$getPageTitle('top-locations.promenade'),
            meta: this.$getPageMetaData('top-locations.promenade'),
        };
    },
};
</script>

<style scoped></style>
